import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AppContext from './context/AppContext';
import SiteReview from './SiteReview';
import '../styles/SiteReviews.css';
import { Button } from 'react-bootstrap';
import { bool } from 'prop-types';
import { MdArrowBackIos, MdArrowForwardIos } from 'react-icons/md';

const BASE_REVIEW_WIDTH = 300;
const BASE_REVIEW_WIDTH_MOBILE = 100;
const BASE_SYM = 'px';
const BASE_SYM_MOB = '%';
const REVIEWS_PER_VIEW = 3;
const REVIEWS_PER_VIEW_MOBILE = 1;
const AUTO_SWITCH_MS = 3500;

export default function SiteReviews({ isMobile }) {
    const { appContext } = useContext(AppContext);
    const { shopperApprovedSealId } = appContext.config;
    const [siteReviews, setSiteReviews] = useState([]);
    const [curView, setCurView] = useState(0);
    const widthByDevice = useMemo(() => isMobile
        ? BASE_REVIEW_WIDTH_MOBILE
        : BASE_REVIEW_WIDTH,
    [BASE_REVIEW_WIDTH_MOBILE, BASE_REVIEW_WIDTH, isMobile]);
    const reviewsByDevice = useMemo(() => isMobile 
        ? REVIEWS_PER_VIEW_MOBILE 
        : REVIEWS_PER_VIEW, 
    [REVIEWS_PER_VIEW_MOBILE, REVIEWS_PER_VIEW, isMobile]);
    const sym = useMemo(() => isMobile 
        ? BASE_SYM_MOB 
        : BASE_SYM, 
    [BASE_SYM_MOB, BASE_SYM, isMobile]);
    const fetchSiteReviews = useCallback(async () => {
        if (!shopperApprovedSealId) 
            return;
        try {
            const params = new URLSearchParams({
                limit: 6,
                page: 0,
            }).toString();
            const response = await fetch(new URL(`/api/reviews/siteReviews/?${params}`, process.env.APP_URL), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const data = await response.json();
            if (data.siteReviews) {
                setSiteReviews(data.siteReviews);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);
    useEffect(() => {
        fetchSiteReviews();
    }, [fetchSiteReviews]);
    const maxViews = useMemo(
        () => {
            const baseVal = Math.ceil(siteReviews.length / reviewsByDevice);
            if (reviewsByDevice % 2 === 0) 
                return baseVal;
            return baseVal - 1;
        }, 
        [reviewsByDevice, siteReviews.length]);
    const next = () => {
        setCurView(curView >= maxViews 
            ? 0 
            : (curView + 1));
    };
    const prev = () => {
        setCurView(curView <= 0 
            ? maxViews 
            : (curView - 1));
    };
    useEffect(() => {
        let autoSwitchTimer = null;
        const autoSwitchFunc = () => {
            next();
        };
        autoSwitchTimer = setTimeout(autoSwitchFunc, AUTO_SWITCH_MS);
        return () => {
            clearTimeout(autoSwitchTimer);
        };
    }, [curView, next]);
    return (<div
        className={'mt-3 mb-3'}
        style={{
            maxWidth: '1400px',
            marginLeft: 'auto',
            marginRight: 'auto',
        }}>
        <h3
            className={'ms-sm-2 ms-md-0'}>Site Reviews</h3>
        <p
            className={'ms-sm-2 ms-md-0'}>Hear what our customers have to say.</p>
        {siteReviews.length > 0 && (<div
            className={'site-reviews'}>
            <Button
                onClick={() => prev()}
                as={MdArrowBackIos} />
            <div
                className={'site-reviews--inner'}
                style={{
                    maxWidth: `${widthByDevice * reviewsByDevice}${sym}`,
                }}>
                <div
                    className={'site-reviews--slider'}
                    style={{
                        left: `-${widthByDevice * curView * reviewsByDevice}${sym}`,
                        width: `${widthByDevice * siteReviews.length}${sym}`,
                    }}>
                    {siteReviews.map((review) => (<SiteReview
                        key={review.id}
                        reviewData={review}
                        width={widthByDevice}
                        symbol={sym} />))}
                </div>
            </div>
            <Button
                onClick={() => next()}
                as={MdArrowForwardIos} />
        </div>)}
    </div>);
}

SiteReviews.propTypes = {
    isMobile: bool,
};